import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';

const reviews = [
  {
    name: "Delphine HEPPLEWHITE",
    rating: 5,
    text: "Pierre est très professionnel, compétent, rigoureux et très compréhensif. Il travaille avec beaucoup de sérieux et de professionnalisme. Je recommande sa Société diagnosti-k sarl à 100% et les yeux fermés. Merci Pierre.",
    date: "il y a un mois",
    source: "Google"
  },
  {
    name: "ANA",
    rating: 5,
    text: "Une personne très professionnelle, qui explique tout dans le détail car il connaît parfaitement son boulot. Il a été ponctuel et tout s'est parfaitement deroulé. Vraiment je recommande les yeux fermés. Merci encore !!",
    date: "il y a un mois",
    source: "Google"
  },
  {
    name: "Laura Lotte",
    rating: 5,
    text: "Dossier réalisé avec professionnalisme et sérieux. Rédaction du dossier complet en moins de 48h. Rapidité, disponibilité et efficacité, vous tenez vos promesses c'est rare en 2024. Je recommande fortement. Merci encore.",
    date: "il y a 5 mois",
    source: "Google"
  },
  {
    name: "Mickaël Tirolien",
    rating: 5,
    text: "Pierre est intervenu au domicile que je loue, très sympa et professionnel. Je recommande !",
    date: "il y a 2 semaines",
    source: "Google"
  },
  {
    name: "Karen Sirguey",
    rating: 5,
    text: "Personne compétente, professionnel et sérieuse. Explique bien les choses. Retour des documents très rapide, le soir même je les avais.",
    date: "il y a 4 mois",
    source: "Google"
  }
];

const Partners = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    // Auto-scroll every 5 seconds
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % reviews.length);
    }, 5000);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearInterval(interval);
    };
  }, []);

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % reviews.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + reviews.length) % reviews.length);
  };

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction < 0 ? '100%' : '-100%',
      opacity: 0
    })
  };

  return (
    <section className="section bg-gradient-to-br from-white to-gray-100">
      <div className="container mx-auto">
        {/* Section Audits Énergétiques */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="flex justify-center mb-8"
          >
            <img
              src="https://photoeasyweb.eu/wp-content/uploads/2025/10/Pied-de-page-Haut-de-page.png"
              alt="DIAGNOSTI-K Logo"
              className="h-32 w-auto object-contain"
            />
          </motion.div>

          <div className="bg-gradient-to-r from-dk-blue to-dk-light-blue text-white rounded-xl p-8 max-w-4xl mx-auto shadow-xl">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold mb-3">Audits Énergétiques Certifiés</h3>
              <p className="text-white/90 text-lg leading-relaxed">
                En plus des DPE classiques, nous réalisons des audits énergétiques certifiés et approfondis 
                pour vous accompagner dans vos projets de rénovation et d'amélioration de la performance énergétique.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white/10 rounded-lg p-4">
                <h4 className="font-semibold mb-2">✓ Analyse détaillée</h4>
                <p className="text-sm text-white/80">Étude complète de votre bâtiment et de ses équipements</p>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <h4 className="font-semibold mb-2">✓ Recommandations personnalisées</h4>
                <p className="text-sm text-white/80">Solutions adaptées à votre budget et vos besoins</p>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <h4 className="font-semibold mb-2">✓ Calcul des économies</h4>
                <p className="text-sm text-white/80">Estimation précise des gains énergétiques et financiers</p>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <h4 className="font-semibold mb-2">✓ Accompagnement aux aides</h4>
                <p className="text-sm text-white/80">Conseil pour l'obtention des subventions disponibles</p>
              </div>
            </div>
            
            <div className="text-center">
              <Link
                to="/contact?subject=audit&message=Bonjour, je souhaiterais obtenir des informations sur vos audits énergétiques et convenir d'un rendez-vous pour étudier mon projet de rénovation. Merci de me recontacter."
                className="inline-flex items-center bg-white text-dk-blue px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                Demander un audit énergétique
              </Link>
            </div>
          </div>
        </motion.div>

        {/* Section Avis Clients */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="section-title text-gray-900">Avis de nos clients</h2>
          <p className="text-lg max-w-3xl mx-auto text-gray-600">
            Découvrez ce que nos clients disent de nos services de diagnostic immobilier
          </p>
        </motion.div>

        <div className="relative px-4">
          <div className="overflow-hidden">
            <div className="flex justify-center gap-6">
              {[-1, 0, 1].map((offset) => {
                const index = (currentIndex + offset + reviews.length) % reviews.length;
                return (
                  <AnimatePresence initial={false} mode="wait" key={index}>
                    <motion.div
                      key={index}
                      custom={offset}
                      variants={slideVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{ duration: 0.5 }}
                      className={`${isMobile ? 'w-[85vw]' : 'w-[400px]'} ${offset !== 0 ? 'hidden md:block' : ''}`}
                    >
                      <div className="bg-white rounded-lg shadow-lg p-6 h-full hover:shadow-xl transition-all duration-300">
                        <div className="flex items-center mb-4">
                          <div className="flex">
                            {[...Array(reviews[index].rating)].map((_, i) => (
                              <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                            ))}
                          </div>
                          <span className="ml-2 text-sm text-gray-500">via {reviews[index].source}</span>
                        </div>
                        <p className="text-gray-700 mb-4">{reviews[index].text}</p>
                        <div className="flex items-center justify-between text-sm mt-auto">
                          <span className="font-medium text-gray-900">{reviews[index].name}</span>
                          <span className="text-gray-500">{reviews[index].date}</span>
                        </div>
                      </div>
                    </motion.div>
                  </AnimatePresence>
                );
              })}
            </div>
          </div>

          {/* Navigation arrows */}
          <button
            onClick={prevSlide}
            className="absolute left-0 top-1/2 -translate-y-1/2 bg-white/80 p-2 rounded-full shadow-lg hover:bg-white transition-colors hidden md:block"
            aria-label="Previous review"
          >
            <ChevronLeft className="h-6 w-6 text-dk-blue" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-0 top-1/2 -translate-y-1/2 bg-white/80 p-2 rounded-full shadow-lg hover:bg-white transition-colors hidden md:block"
            aria-label="Next review"
          >
            <ChevronRight className="h-6 w-6 text-dk-blue" />
          </button>

          {/* Dots navigation */}
          <div className="flex justify-center mt-4 gap-2">
            {reviews.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentIndex ? 'bg-dk-blue scale-125' : 'bg-gray-300'
                }`}
                aria-label={`Go to review ${index + 1}`}
              />
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <a 
            href="https://www.google.com/search?q=Diagnostics+Immobiliers+-+Entreprise+Diagnosti-K+-+DPE,+Amiante,+Plomb,+%C3%89lectricit%C3%A9,+Gaz,+Termites,+Mesurage+Avis"
            target="_blank"
            rel="noopener noreferrer"
            className="text-dk-blue hover:text-dk-light-blue transition-colors"
          >
            Voir tous nos avis sur Google →
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Partners;